import { useQuery } from '@apollo/client/react';
import { dateSchema } from '@optools/schemas/date';
import { gamePkSchema } from '@optools/schemas/game';
import { GET_LOGGER_GAME } from '@optools/services/operator-gateway/logger/queries';
import {
  LoggerGame,
  LoggerGamePipeline,
  LoggerGameTeam,
} from '@optools/services/operator-gateway/logger/types';
import {
  MediaFeedType,
  MediaState,
  MediaType,
  PipelineStatus,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { FeedConsole } from '@optools/tools/logger/components/feed-console';
import { getMediaId } from '@optools/utils/pipeline';
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useMemo, useState } from 'react';

const getTeamData = (
  game: LoggerGame,
  mediaFeedType: MediaFeedType,
): LoggerGameTeam | undefined => {
  if (mediaFeedType === MediaFeedType.AWAY) {
    return game?.away;
  }
  if (mediaFeedType === MediaFeedType.HOME) {
    return game?.home;
  }
  return undefined;
};

const getPipeline = (
  game: LoggerGame,
  mediaFeedType: MediaFeedType,
): LoggerGamePipeline | undefined =>
  game?.pipelines.find((pipeline) => [mediaFeedType, 'NATIONAL'].includes(pipeline.feedType));

export const Route = createFileRoute('/_auth/logger/$gamePk')({
  component: RouteComponent,
  params: { parse: zodValidator(gamePkSchema).parse },
  validateSearch: zodValidator(dateSchema),
});

export function RouteComponent() {
  const { date } = useSearch({ from: '/_auth/logger/$gamePk' });
  const { gamePk } = useParams({ from: '/_auth/logger/$gamePk' });
  const [game, setGame] = useState<LoggerGame | null>(null);
  const [pipelines, setPipelines] = useState<LoggerGamePipeline[]>([]);
  const activePipeline = game?.pipelines.find((pipeline) =>
    [
      StreamState.PROGRAM_DONE,
      StreamState.PROGRAM_ON,
      StreamState.STREAM_ON,
      StreamState.STREAM_DONE,
    ].includes(pipeline.streamState),
  );
  const shouldPollData = !!activePipeline;

  const { data } = useQuery(GET_LOGGER_GAME, {
    pollInterval: shouldPollData ? 1000 : undefined,
    skip: !gamePk,
    variables: { gamePk, scheduleDate: date },
  });

  useEffect(() => {
    const loadedGame = data?.game;
    if (loadedGame) {
      setGame(loadedGame);
    }
    const pipelines = [...(loadedGame?.pipelines ?? [])].filter(
      (pipeline) =>
        ![PipelineStatus.ENDED, PipelineStatus.ENDING, PipelineStatus.ARCHIVED].includes(
          pipeline.status,
        ) && ![StreamState.STREAM_OFF, StreamState.EVENT_ARCHIVE].includes(pipeline.streamState),
    );
    const sortedPipelines = pipelines.sort((a, b) => a.feedType.localeCompare(b.feedType)) ?? [];
    setPipelines(sortedPipelines);
  }, [data?.game]);

  const videoFeeds = useMemo(
    () => game?.content?.filter((item) => item?.mediaState?.mediaType === MediaType.VIDEO),
    [game],
  );

  if (
    !game ||
    !videoFeeds ||
    (videoFeeds.every((item) => item.mediaState.state === MediaState.OFF) &&
      !game.pipelines?.length)
  ) {
    return (
      <p className="mt-20 text-center text-3xl">No Media Available to Log, Check back Later</p>
    );
  } else if (
    videoFeeds.every((item) => item.mediaState.state === MediaState.ARCHIVE) &&
    (!game.pipelines?.length ||
      game.pipelines.every((pipeline) => pipeline.status === PipelineStatus.ARCHIVED))
  ) {
    return <p className="mt-20 text-center text-3xl">Feeds have ended, no Media available</p>;
  } else if (!game.pipelines?.length) {
    return <p className="mt-20 text-center text-3xl">No Active Pipelines</p>;
  }

  const feedConsoles = pipelines.map((pipeline, i) => {
    const { feedType } = pipeline;
    const mediaId = getMediaId(pipeline);
    const teamData = getTeamData(game, feedType);
    const item = getPipeline(game, feedType);
    const feed = game?.feeds?.find((item) => item.feedType === feedType);
    return item ? (
      <div
        key={mediaId}
        className="flex w-full flex-col items-center"
        data-testid={`feed-${i + 1}`}
        style={{ flexBasis: `${100 / pipelines.length}%` }}>
        <FeedConsole
          content={game.content}
          feed={feed}
          pipeline={item}
          side={feedType}
          teamData={teamData}
        />
      </div>
    ) : null;
  });

  return (
    <div
      className="mx-auto my-0 grid w-full max-w-[1200px] justify-items-center gap-2"
      data-testid="shadcn-experience"
      style={{ gridTemplateColumns: `repeat(${feedConsoles.length}, 1fr)` }}>
      {feedConsoles}
    </div>
  );
}
